// Implements: F-UIX-008, F-UIX-009, F-UIX-010
/**
 * The container of the login screen: it owns the three values and the machine.
 *
 * **No failure path resets the form.** Email, password and code live in this
 * component's state from the first keystroke to the successful attempt, and
 * every refusal leaves them exactly where they were, because the second
 * attempt sends all three in the same request: there is no half-authenticated
 * session to resume (design section 5).
 *
 * The password lives HERE and nowhere else. It is never written to
 * `localStorage`, to `sessionStorage` or to the URL, and
 * `LoginContainer.storage.test.tsx` audits that after a failed attempt and
 * after a successful one.
 *
 * Navigation is a prop. The router arrives in slice 3b (task 4.10), and until
 * it does, "where a successful login goes" is a decision the caller makes:
 * `onSuccess` receives the user the backend recognised.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { UserSummary } from "../../../types/api";
import { LoginForm } from "../components/LoginForm";
import type { LoginStep } from "../components/LoginForm";
import { RateLimitNotice } from "../components/RateLimitNotice";
import { SecondFactorField } from "../components/SecondFactorField";
import { useLogin } from "../hooks/useLogin";
import type { LoginState } from "../hooks/useLogin";

export interface LoginContainerProps {
  /** Called once, with the user, when the attempt succeeded. */
  readonly onSuccess?: ((user: UserSummary) => void) | undefined;
  /** P2: the «¿Olvidaste tu contraseña?» link the page hands down (the form stays router-free). */
  readonly forgotPassword?: ReactNode;
}

export function LoginContainer({ onSuccess, forgotPassword }: LoginContainerProps): ReactElement {
  const { t } = useTranslation();
  // Once the code field is on screen it stays there: `secondFactorRevealed` is
  // the hook's memory of what the server said, and it outlives the `idle` that
  // every keystroke goes back to.
  const { state, secondFactorRevealed, submit, reset } = useLogin();

  const [email, setEmail] = useState("");
  // A ref, not state: the password field is uncontrolled so its value never reaches the markup's
  // `value` attribute, and nothing on screen depends on it between keystrokes.
  const password = useRef("");
  const [code, setCode] = useState("");
  const emailRef = useRef<HTMLInputElement | null>(null);
  // Set by "Volver", consumed once the credentials step is on screen again: the button that had
  // the focus has just unmounted, so the focus goes to the first credential field.
  const returnFocus = useRef(false);

  // Which step the card shows. The server asking for a code (first time, or again after a rejected
  // one) moves it to `code`; "Volver" moves it back. Derived while rendering from the status that
  // just arrived, so no effect has to chase the machine.
  const [step, setStep] = useState<LoginStep>("credentials");
  const [seenStatus, setSeenStatus] = useState(state.status);
  if (seenStatus !== state.status) {
    setSeenStatus(state.status);
    if (state.status === "secondFactorRequired" || state.status === "secondFactorInvalid") setStep("code");
  }

  useEffect(() => {
    if (step !== "credentials" || !returnFocus.current) return;
    returnFocus.current = false;
    emailRef.current?.focus();
  }, [step]);

  // Announced once. `onSuccess` is usually an inline arrow, so a bare effect
  // dependency on it would fire again on every render that follows.
  const announced = useRef(false);
  useEffect(() => {
    if (state.status !== "success" || announced.current) return;
    announced.current = true;
    onSuccess?.(state.user);
  }, [state, onSuccess]);

  /** Every edit clears the previous outcome — except the wait, which is the server's. */
  const edit = useCallback(
    (set: (value: string) => void) =>
      (value: string): void => {
        set(value);
        reset();
      },
    [reset],
  );

  const handlePasswordChange = useCallback(
    (value: string): void => {
      password.current = value;
      reset();
    },
    [reset],
  );

  const handleSubmit = useCallback((): void => {
    void submit({ email, password: password.current, totpCode: code });
  }, [submit, email, code]);

  const locked = state.status === "locked";
  const submitting = state.status === "submitting";
  // Closed for two different reasons that look the same to a field: the server
  // is making us wait, or the server is already answering. In both, the values
  // stay exactly where they are — a disabled input keeps what it holds.
  const closed = locked || submitting;

  return (
    <LoginForm
      email={email}
      emailRef={emailRef}
      onEmailChange={edit(setEmail)}
      onPasswordChange={handlePasswordChange}
      onSubmit={handleSubmit}
      submitting={submitting}
      disabled={closed}
      errorMessage={errorMessage(state, t)}
      step={step}
      onBack={() => {
        // The code belongs to the attempt the person is leaving: it is dropped, never resent with
        // credentials that may have changed (UX-12 verifier finding 1).
        setCode("");
        returnFocus.current = true;
        setStep("credentials");
      }}
      secondFactor={
        secondFactorRevealed && step === "code" ? (
          <SecondFactorField
            value={code}
            onChange={edit(setCode)}
            invalid={state.status === "secondFactorInvalid"}
            disabled={closed}
          />
        ) : null
      }
      notice={locked ? <RateLimitNotice seconds={state.secondsRemaining} /> : null}
      forgotPassword={forgotPassword}
    />
  );
}

/**
 * The sentence the form shows, or nothing.
 *
 * A `429` deliberately produces NO error sentence: it is a wait, and it is
 * already on screen as one (DD8). A second factor produces none either — the
 * field itself is the message.
 */
function errorMessage(
  state: LoginState,
  translate: ReturnType<typeof useTranslation>["t"],
): string | undefined {
  if (state.status !== "error") return undefined;
  switch (state.kind) {
    case "invalidCredentials":
      return translate("auth.login.error.invalidCredentials");
    case "organizationsSuspended":
      // Owner 2026-10-19 (9A): the backend composes this sentence, naming the support contact when
      // `SUPPORT_CONTACT_EMAIL` is set; it is told only once every factor was proven.
      return state.failure.message.trim() === ""
        ? translate("auth.login.error.organizationsSuspended")
        : state.failure.message;
    case "validation":
      return translate("auth.login.error.validation");
    case "unexpected":
      return translate("auth.login.error.unexpected");
  }
}
